import React from 'react'
import { connect } from 'react-redux'
import {
  EntityStatus,
  EntityTypeEnum,
  ReferenceDataManagementEvent,
  TradingEntityClearing,
  TradingEntityClearingDetail,
  WorkflowDetail,
  WorkflowActions
} from 'api'
import { ActionItem, SplitButtonDropdown } from 'view/components/menus'
import { ActionsButtonProps } from 'view/components/datagrid'
import { ModalConsumerProps, withModal } from 'view/shared/modal'
import { TradingEntityClearingDialog } from './TradingEntityClearingDialog'
import { NotificationActions, NotificationType, ShowDialogArgs } from 'store/notification'
import { Locale, Translation } from 'locale'

export interface ClearingPanelActionsButtonProps
  extends ModalConsumerProps,
    ActionsButtonProps<WorkflowDetail<TradingEntityClearingDetail>> {
  onShowDialog: (dialogArgs: ShowDialogArgs) => unknown
  context: {
    workflowId: string
    tradingEntityId: string
  }
}

const mapStateToProps = null

const mapDispatchToProps = {
  onShowDialog: NotificationActions.showDialog
}

const canEditDetails = (item: WorkflowDetail<TradingEntityClearingDetail>) =>
  item.availableEvents.includes(ReferenceDataManagementEvent.ENTITY_UPDATE)

const canEnable = (item: WorkflowDetail<TradingEntityClearingDetail>) =>
  item.availableEvents.includes(ReferenceDataManagementEvent.ENTITY_UPDATE) && item.tradingEntityClearing.status !== EntityStatus.ENABLED

const canDisable = (item: WorkflowDetail<TradingEntityClearingDetail>) =>
  item.availableEvents.includes(ReferenceDataManagementEvent.ENTITY_UPDATE) && item.tradingEntityClearing.status === EntityStatus.ENABLED

export const TradingEntityClearingPanelActionsButtonComponent: React.FunctionComponent<ClearingPanelActionsButtonProps> = props => {
  const {
    items,
    onShowDialog,
    showModal,
    context: { workflowId, tradingEntityId }
  } = props

  const editItems = items.filter(item => canEditDetails(item))
  const enableItems = items.filter(item => canEnable(item))
  const disableItems = items.filter(item => canDisable(item))

  const handleShowItemDialog = (tradingEntityClearingDetail: TradingEntityClearingDetail[]) => {
    const { id, type } = tradingEntityClearingDetail[0].tradingEntityClearing

    showModal(
      <TradingEntityClearingDialog
        tradingEntityClearingType={type}
        tradingEntityId={tradingEntityId}
        entityId={id}
        workflowId={workflowId}
      />
    )
  }

  const handleEnable = (enableTradingEntClearings: TradingEntityClearingDetail[]) => {
    const titleText = Locale.translate(Translation.TC_PROMPT_TITLE_ACTIVATE_SINGLE_TRADING_ENTITY_CLEARING)

    const messageText = Locale.translate(Translation.SC_PROMPT_MSG_ACTIVATE_SINGLE_TRADING_ENTITY_CLEARING, {
      clearingHouseName: enableTradingEntClearings[0].clearingHouse && enableTradingEntClearings[0].clearingHouse.publishedName
    })

    const enableActions = enableTradingEntClearings.map(item => {
      return WorkflowActions.createEntityUpdateReferenceData<TradingEntityClearing>({
        entityId: item.tradingEntityClearing.id,
        entityType: EntityTypeEnum.TRADING_ENTITY_CLEARING,
        updatedEntity: {
          ...item.tradingEntityClearing,
          status: EntityStatus.ENABLED
        },
        workflowId
      })
    })

    onShowDialog({
      type: NotificationType.Confirm,
      titleText: titleText,
      messageText: messageText,
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: enableActions
      }
    })
  }

  const handleDisable = (disableTradingEntClearings: TradingEntityClearingDetail[]) => {
    const titleText = Locale.translate(Translation.TC_PROMPT_TITLE_SUSPEND_SINGLE_TRADING_ENTITY_CLEARING)

    const messageText = Locale.translate(Translation.SC_PROMPT_MSG_SUSPEND_SINGLE_TRADING_ENTITY_CLEARING, {
      clearingHouseName: disableTradingEntClearings[0].clearingHouse && disableTradingEntClearings[0].clearingHouse.publishedName
    })

    const disableActions = disableTradingEntClearings.map(item => {
      return WorkflowActions.createEntityUpdateReferenceData<TradingEntityClearing>({
        entityId: item.tradingEntityClearing.id,
        entityType: EntityTypeEnum.TRADING_ENTITY_CLEARING,
        updatedEntity: {
          ...item.tradingEntityClearing,
          status: EntityStatus.DISABLED
        },
        workflowId
      })
    })

    onShowDialog({
      type: NotificationType.Confirm,
      titleText: titleText,
      messageText: messageText,
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: disableActions
      }
    })
  }

  const handleDelete = (enableTradingEntClearings: TradingEntityClearingDetail[]) => {
    const titleText = Locale.translate(Translation.TC_PROMPT_TITLE_DELETE_SINGLE_TRADING_ENTITY_CLEARING)

    const messageText = Locale.translate(Translation.SC_PROMPT_MSG_DELETE_SINGLE_TRADING_ENTITY_CLEARING, {
      clearingHouseName: enableTradingEntClearings[0].clearingHouse && enableTradingEntClearings[0].clearingHouse.publishedName
    })

    const deleteActions = enableTradingEntClearings.map(item => {
      return WorkflowActions.createEntityDeleteReferenceData(
        [
          {
            entityType: EntityTypeEnum.TRADING_ENTITY_CLEARING,
            entityId: item.tradingEntityClearing.id
          }
        ],
        workflowId
      )
    })

    onShowDialog({
      type: NotificationType.Confirm,
      titleText: titleText,
      messageText: messageText,
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: deleteActions
      }
    })
  }

  const actions: ActionItem[] = [
    {
      label: Locale.translate(Translation.TC_ACTION_EDIT_DETAILS),
      onClick: () => handleShowItemDialog(editItems),
      enabled: editItems.length === 1
    },
    {
      label: Locale.translate(Translation.TC_ACTION_SUSPEND, disableItems.length),
      onClick: () => handleDisable(disableItems),
      enabled: disableItems.length > 0
    },
    {
      label: Locale.translate(Translation.TC_ACTION_ACTIVATE, enableItems.length),
      onClick: () => handleEnable(enableItems),
      enabled: enableItems.length > 0
    },
    {
      label: Locale.translate(Translation.TC_ACTION_DELETE, editItems.length),
      onClick: () => handleDelete(editItems),
      enabled: editItems.length > 0
    }
  ]

  return <SplitButtonDropdown actions={actions} maxButtons={1} />
}

export const TradingEntityClearingPanelActionsButton = withModal(
  connect(mapStateToProps, mapDispatchToProps)(TradingEntityClearingPanelActionsButtonComponent)
)
